import { FolderGit2, History } from '@orgus/ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { OS_LABEL, machineLiveness } from '@orgus/protocol';
import { runAction } from '../../lib/api.ts';
import { Button } from '@orgus/ui';
import { Dialog } from '@orgus/ui';
import { ErrorState } from '@orgus/ui';
import { Input, Select } from '@orgus/ui';
import { Pressable } from '@orgus/ui';
import { RelativeTime } from '@orgus/ui';
import { shortPath } from '@orgus/ui';
import { useMachines } from '../../lib/store.ts';
import { useVisibleSuggestions } from './hidden.ts';
import { toast } from '@orgus/ui';
import { useNow } from '@orgus/ui';
import { mockProjectAdded } from './mockEffects.ts';
import { pathError, pathPlaceholder } from './model.ts';

/** Add a project: pick the machine, then the folder. Folders the machine found are one click away. */
export function AddProjectDialog({ open, onOpenChange, initialMachineId }: { open: boolean; onOpenChange: (v: boolean) => void; initialMachineId?: string }) {
  const machines = useMachines();
  const suggestions = useVisibleSuggestions();
  const now = useNow(10_000);
  const online = machines.filter((m) => machineLiveness(m, now) === 'online');
  const [machineId, setMachineId] = useState('');
  const [path, setPath] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const pathRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setMachineId(initialMachineId ?? online[0]?.id ?? '');
    setPath('');
    setTouched(false);
    setError(null);
    setBusy(false);
    // Reset only when the dialog opens.
  }, [open]);

  const machine = machines.find((m) => m.id === machineId);
  const err = pathError(path, machine?.os);
  const found = useMemo(() => suggestions.filter((s) => s.machineId === machineId), [suggestions, machineId]);
  const valid = !!machine && !err;

  const submit = async () => {
    setTouched(true);
    if (!valid || busy || !machine) return;
    setBusy(true);
    setError(null);
    const p = path.trim();
    try {
      await runAction(machine.id, 'project.add', { machineId: machine.id, path: p });
      mockProjectAdded(machine.id, p);
      toast.success('Project added', `Watching ${shortPath(p)} on ${machine.name}.`);
      onOpenChange(false);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add project"
      description="Orgus watches a git repository on one machine: its branches, worktrees and sessions."
      dismissible={!busy}
      initialFocus={() => pathRef.current}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!valid} onClick={submit} shortcut={['mod', 'enter']}>
            Add project
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void submit();
          }
        }}
      >
        <Select
          label="Machine"
          value={machineId}
          onValueChange={setMachineId}
          placeholder={machines.length ? 'Choose a machine' : 'No machines yet'}
          options={machines.map((m) => {
            const ok = machineLiveness(m, now) === 'online';
            return { value: m.id, label: `${m.name} (${OS_LABEL[m.os]}${ok ? '' : ', offline'})`, disabled: !ok };
          })}
          hint={
            !online.length ? (
              <>
                No machine is online. Start the agent on one first, or{' '}
                <Link to="/machines" onClick={() => onOpenChange(false)} className="text-accent-text underline-offset-2 hover:underline">
                  add a machine
                </Link>
                .
              </>
            ) : undefined
          }
        />
        <Input
          ref={pathRef}
          label="Folder"
          value={path}
          onChange={(e) => setPath(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={pathPlaceholder(machine?.os)}
          icon={<FolderGit2 />}
          spellCheck={false}
          autoComplete="off"
          error={touched && path.trim() ? err : undefined}
          hint={touched && path.trim() && err ? undefined : 'The folder of a git repository on that machine. Its worktrees are found automatically.'}
        />
        {found.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-fg-muted">Recent Claude Code folders on {machine?.name}</span>
            <ul className="flex flex-col overflow-hidden rounded-md border border-border">
              {found.map((s) => (
                <li key={s.path} className="border-b border-border-subtle last:border-b-0">
                  <Pressable
                    onClick={() => {
                      setPath(s.path);
                      setTouched(true);
                      pathRef.current?.focus();
                    }}
                    aria-label={`Use ${s.path}`}
                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-hover"
                  >
                    <History className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-base text-fg">{s.name}</span>
                      <span className="block truncate font-mono text-xs text-fg-subtle">{s.path}</span>
                    </span>
                    <span className="shrink-0 text-xs text-fg-subtle">
                      <RelativeTime ts={s.lastSessionAt} fallback="" />
                    </span>
                  </Pressable>
                </li>
              ))}
            </ul>
          </div>
        )}
        {error != null && <ErrorState variant="inline" error={error} />}
      </form>
    </Dialog>
  );
}
